import React, { useState, useEffect } from 'react';
import {
  Box, Button, Typography, Popover, IconButton, TextField,
  Autocomplete, Chip, Slider, Menu, MenuItem, ListItemIcon, ListItemText,
  Collapse, Grid,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stack,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  TuneRounded as FilterAltIcon,
  Add as AddIcon,
  EditOutlined as EditIcon,
  DeleteOutlineRounded as DeleteIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  BookmarkBorderRounded as BookmarkBorderIcon,
  FlagRounded as StatusIcon,
  ApartmentRounded as BhkIcon,
  Pool as AmenitiesIcon,
  EventRounded as PropertyAgeIcon,
  ExploreRounded as FacingIcon,
  CampaignRounded as SourceIcon,
  StyleRounded as TagsIcon,
  SaveRounded as SaveIcon,
  AccountBalanceWalletRounded as CurrencyRupeeIcon,
  CloseRounded as CloseIcon,
  ScheduleRounded as TimeIcon,
  WorkOutlineRounded as WorkIcon,
  PersonOutlineRounded as PersonIcon,
  Diversity3Rounded as ChannelPartnerIcon,
  WhatshotRounded as UrgencyIcon,
  ThermostatRounded as LcpIcon,
  DirectionsWalkRounded as VisitIcon,
  LocationOnRounded as LocationIcon,
  CategoryRounded as PropertyTypeIcon,
  TrackChangesRounded as IntentIcon,
  PriceChangeRounded as BudgetRangeIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

import { useLead } from '../../contexts/LeadContext';
import { useModal } from '../../contexts/ModalContext';
import AgentSelect from '../../components/common/AgentSelect';
import { LEAD_THEME as T, toTitleCase } from '../../utils/lead_helper_v2';

// ── Filter icon color map (each filter gets a unique accent) ──────────────
const FILTER_ICON_COLORS = {
  lead_stage:       { color: '#6366F1', bg: '#EEF2FF' },
  status:           { color: '#F59E0B', bg: '#FFFBEB' },
  last_contacted:   { color: '#06B6D4', bg: '#ECFEFF' },
  agent_id:         { color: '#8B5CF6', bg: '#F5F3FF' },
  source:           { color: '#EC4899', bg: '#FDF2F8' },
  channel_partner:  { color: '#14B8A6', bg: '#F0FDFA' },
  lead_urgency:     { color: '#EF4444', bg: '#FEF2F2' },
  lead_lcp:         { color: '#F97316', bg: '#FFF7ED' },
  occupation:       { color: '#64748B', bg: '#F8FAFC' },
  bhk_type:         { color: '#3B82F6', bg: '#EFF6FF' },
  tags:             { color: '#10B981', bg: '#ECFDF5' },
  site_visit_frequency:   { color: '#A855F7', bg: '#FAF5FF' },
  budget:           { color: '#0A6B3E', bg: '#E5F5EC' },
  budget_ranges:    { color: '#059669', bg: '#ECFDF5' },
  locations:        { color: '#DC2626', bg: '#FEF2F2' },
  property_types:   { color: '#7C3AED', bg: '#F5F3FF' },
  intent:           { color: '#0284C7', bg: '#F0F9FF' },
};

const chipStyles = {
  height: '22px',
  borderRadius: '6px',
  '& .MuiChip-label': { fontSize: '0.7rem', px: 0.75, fontWeight: 500 },
  '& .MuiChip-deleteIcon': { fontSize: '0.8rem' },
};

const sectionHeaderStyles = {
  fontSize: '0.72rem',
  fontWeight: 600,
  textTransform: 'none',
  letterSpacing: '0.01em',
};

const autocompleteStyles = {
  '& .MuiInputBase-root': {
    fontSize: '0.72rem',
    minHeight: '30px',
    fontWeight: 400,
    borderRadius: '8px',
  },
  '& .MuiInputBase-input': {
    fontSize: '0.72rem',
    fontWeight: 400,
  },
  '& .MuiAutocomplete-endAdornment': {
    top: '50%',
    transform: 'translateY(-50%)',
  },
  '& .MuiAutocomplete-popupIndicator': { padding: '2px' },
  '& .MuiAutocomplete-clearIndicator': { padding: '2px' },
};

/** Autocomplete popper layout only — fonts from `theme` typography.dropdown */
const filterAutocompleteSlotProps = {
  listbox: {
    sx: {
      py: 0.5,
      maxHeight: 280,
    },
  },
  paper: {
    sx: {
      borderRadius: '10px',
    },
  },
};

const textFieldStyles = {
  '& .MuiInputBase-root': {
    fontSize: '0.72rem',
    minHeight: '30px',
    fontWeight: 400,
    borderRadius: '8px',
  },
  '& .MuiInputBase-input': {
    fontSize: '0.72rem',
    fontWeight: 400,
  },
};

/** Non-empty filter count for saved-search subtitle */
const countSavedSearchCriteria = (criteria) => {
  if (!criteria || typeof criteria !== 'object') return 0;
  let n = 0;
  Object.entries(criteria).forEach(([key, value]) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      if (value.length > 0) n += 1;
      return;
    }
    if (typeof value === 'object') {
      if (key === 'budget' && (value.min_value != null || value.max_value != null)) n += 1;
      else if (key === 'last_contacted' && (value.preset || value.start_time || value.end_time)) n += 1;
      else if (Object.keys(value).length > 0) n += 1;
      return;
    }
    n += 1;
  });
  return n;
};

// Filters shown in "More Filters" popover only.
// Removed: lead_stage, status (in sidebar), last_contacted, budget_ranges, locations,
// property_types, intent (all inline in toolbar), budget slider (replaced by budget_ranges),
// occupation, bhk_type (not needed).
const filterOrder = [
  'agent_id',
  'source',
  'channel_partner',
  'lead_urgency',
  'lead_lcp',
  'tags',
  'site_visit_frequency',
];

const FILTER_LABELS = {
  budget_ranges: 'Budget Range',
  locations: 'Location',
  property_types: 'Property Type',
  intent: 'Intent / Purpose',
};

const BUDGET_SLIDER_MIN = 2500000;
const BUDGET_SLIDER_MAX = 100000000;
/** Step: ₹5L (5 × 1L) */
const BUDGET_SLIDER_STEP = 500000;

/**
 * True if `filters` has at least one non-empty criterion (empty arrays / default budget don't count).
 * Exported for toolbar "Save search" enablement (same rules as the Save button in the filter panel).
 */
export const hasMeaningfulFilters = (f) => {
  if (!f || typeof f !== 'object') return false;
  for (const key of Object.keys(f)) {
    const v = f[key];
    if (v == null) continue;
    if (Array.isArray(v)) {
      if (v.length > 0) return true;
      continue;
    }
    if (key === 'budget' && typeof v === 'object') {
      const { min_value, max_value } = v;
      const isDefault =
        (min_value === BUDGET_SLIDER_MIN || min_value === 0 || min_value === undefined) &&
        (max_value === BUDGET_SLIDER_MAX || max_value === undefined);
      if (!isDefault) return true;
      continue;
    }
    if (key === 'last_contacted' && typeof v === 'object') {
      if (v.preset || v.start_time || v.end_time) return true;
      continue;
    }
    if (typeof v === 'object' && Object.keys(v).length > 0) return true;
    if (typeof v !== 'object') return true;
  }
  return false;
};

const LeadFilters = ({
  open,
  anchorEl,
  onClose,
  defaultFilters = {},
  agents = [],
  statusCounts = {},
  setStatusCounts,
  dateRangeState,
  onFiltersChange,
  /** Opens shared SaveLeadSearchDialog with current panel filter draft */
  onOpenSaveSearch,
}) => {
  const {
    appliedFilters,
    setAppliedFilters,
    fetchLeads,
    updateSavedSearch,
    deleteSavedSearch,
    applySavedSearch,
    fetchStatusCounts
  } = useLead();

  const { openSuccessModal, openErrorModal } = useModal();

  // State
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedSearchId, setExpandedSearchId] = useState(null);
  const [editingSearchId, setEditingSearchId] = useState(null);
  const [editingFilters, setEditingFilters] = useState({});
  const [editingName, setEditingName] = useState('');
  const [addFilterAnchorEl, setAddFilterAnchorEl] = useState(null);
  const [budgetRange, setBudgetRange] = useState([2500000, 100000000]); // Default: 25L to 10Cr
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialFilters, setInitialFilters] = useState({});
  const [updatingSearch, setUpdatingSearch] = useState(false);
  const [lastContactedFilter, setLastContactedFilter] = useState('');
  const [customStartDateTime, setCustomStartDateTime] = useState(null);
  const [customEndDateTime, setCustomEndDateTime] = useState(null);
  // Sync filters with applied filters when modal opens
  useEffect(() => {
    if (open) {
      const normalizedFilters = { ...appliedFilters };

      // ------------------------------------------------------
      // Backward/interop mapping for agent & channel filters
      // ------------------------------------------------------
      // Backend and saved-search payloads now use `assigned_to`
      // and `channel_partners`, while this UI expects `agent_id`
      // and `channel_partner` in its local state.
      if (normalizedFilters.assigned_to && !normalizedFilters.agent_id) {
        normalizedFilters.agent_id = normalizedFilters.assigned_to;
      }
      if (normalizedFilters.agents && !normalizedFilters.agent_id) {
        normalizedFilters.agent_id = normalizedFilters.agents;
      }

      if (normalizedFilters.channel_partners && !normalizedFilters.channel_partner) {
        normalizedFilters.channel_partner = normalizedFilters.channel_partners;
      }
      const arrayFields = ['status', 'lead_stage', 'tags', 'agent_id', 'source', 'bhk_type', 'lead_urgency', 'lead_lcp', 'occupation', 'site_visit_frequency', 'channel_partner', 'budget_ranges', 'locations', 'property_types', 'intent'];

      arrayFields.forEach(field => {
        if (normalizedFilters[field] !== undefined && normalizedFilters[field] !== null && !Array.isArray(normalizedFilters[field])) {
          normalizedFilters[field] = [normalizedFilters[field]];
        }
      });

      if (normalizedFilters.last_contacted_at__isnull) delete normalizedFilters.last_contacted_at__isnull;

      // Convert raw values into option objects for UI label rendering
      // (Autocomplete shows label when values are option objects)
      Object.keys(normalizedFilters).forEach((key) => {
        if (!Array.isArray(normalizedFilters[key])) return;
        const opts = defaultFilters?.[key];
        if (!Array.isArray(opts) || opts.length === 0) return;

        normalizedFilters[key] = normalizedFilters[key].map((v) => {
          // Already an option object
          if (typeof v === 'object' && v !== null) return v;
          const match = opts.find((o) => (o?.value ?? o) === v);
          return match ?? v;
        });
      });

      setFilters(normalizedFilters || {});
      setInitialFilters(normalizedFilters || {});
      setHasChanges(false);

      if (appliedFilters?.budget) {
        const { min_value, max_value } = appliedFilters.budget;
        setBudgetRange([
          min_value || 2500000,
          max_value || 100000000
        ]);
      } else {
        setBudgetRange([2500000, 100000000]); // Reset to default
      }

      // Reset last contacted filter
      if (appliedFilters?.last_contacted?.preset) {
        const { preset, start_time, end_time } = appliedFilters.last_contacted;
        setLastContactedFilter(preset);

        if (preset === 'custom') {
          setCustomStartDateTime(start_time ? dayjs(start_time) : null);
          setCustomEndDateTime(end_time ? dayjs(end_time) : null);
        }
      } else if (appliedFilters?.last_contacted) {
        // Fallback for filters saved without a preset
        setLastContactedFilter('custom');
        const { start_time, end_time } = appliedFilters.last_contacted;
        setCustomStartDateTime(start_time ? dayjs(start_time) : null);
        setCustomEndDateTime(end_time ? dayjs(end_time) : null);
      } else {
        setLastContactedFilter('');
        setCustomStartDateTime(null);
        setCustomEndDateTime(null);
      }
    }
  }, [open, appliedFilters]);

  // Fetch data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        if (!statusCounts || !statusCounts.saved_searches) {
          await fetchStatusCounts(dateRangeState).then((data) => {
            setStatusCounts(data);
          });
        }
        // Initialize filters from applied filters (this should not override the normalized filters from the first useEffect)
        // The first useEffect already handles the normalization, so we don't need to do it again here
        // This useEffect is mainly for loading statusCounts data
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, statusCounts?.saved_searches?.length]);

  // Add effect to check for changes whenever filters change
  useEffect(() => {
    // Only run this after initial load
    if (!loading) {
      // Deep comparison of filters
      const initialKeys = Object.keys(initialFilters);
      const currentKeys = Object.keys(filters);

      // Check if keys are different
      if (initialKeys.length !== currentKeys.length) {
        setHasChanges(true);
        return;
      }

      // Check if values are different
      let changed = false;

      for (const key of currentKeys) {
        const initialValue = initialFilters[key];
        const currentValue = filters[key];

        // Handle different types of values
        if (key === 'budget') {
          // Compare budget min/max
          if (!initialValue || !currentValue) {
            changed = true;
            break;
          }

          if (initialValue.min_value !== currentValue.min_value ||
            initialValue.max_value !== currentValue.max_value) {
            changed = true;
            break;
          }
        } else if (Array.isArray(currentValue)) {
          // Compare arrays
          if (!initialValue || !Array.isArray(initialValue) ||
            initialValue.length !== currentValue.length ||
            !initialValue.every((val, i) => val === currentValue[i])) {
            changed = true;
            break;
          }
        } else {
          // Compare other values
          if (initialValue !== currentValue) {
            changed = true;
            break;
          }
        }
      }

      setHasChanges(changed);
    }
  }, [filters, initialFilters, loading]);

  // Helper functions
  const getFilterIcon = (filterKey) => {
    const palette = FILTER_ICON_COLORS[filterKey] || { color: T.green, bg: T.greenSf };
    const iconSx = { fontSize: 14, color: palette.color };
    const pillSx = {
      width: 26, height: 26, borderRadius: '7px', bgcolor: palette.bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    };

    const iconMap = {
      status:           <StatusIcon sx={iconSx} />,
      lead_stage:       <FilterAltIcon sx={iconSx} />,
      bhk_type:         <BhkIcon sx={iconSx} />,
      amenities:        <AmenitiesIcon sx={iconSx} />,
      property_age:     <PropertyAgeIcon sx={iconSx} />,
      facing:           <FacingIcon sx={iconSx} />,
      source:           <SourceIcon sx={iconSx} />,
      tags:             <TagsIcon sx={iconSx} />,
      budget:           <CurrencyRupeeIcon sx={iconSx} />,
      agent_id:         <PersonIcon sx={iconSx} />,
      last_contacted:   <TimeIcon sx={iconSx} />,
      occupation:       <WorkIcon sx={iconSx} />,
      site_visit_frequency: <VisitIcon sx={iconSx} />,
      channel_partner:  <ChannelPartnerIcon sx={iconSx} />,
      lead_urgency:     <UrgencyIcon sx={iconSx} />,
      lead_lcp:         <LcpIcon sx={iconSx} />,
      budget_ranges:    <BudgetRangeIcon sx={iconSx} />,
      locations:        <LocationIcon sx={iconSx} />,
      property_types:   <PropertyTypeIcon sx={iconSx} />,
      intent:           <IntentIcon sx={iconSx} />,
    };

    return (
      <Box sx={pillSx}>
        {iconMap[filterKey] || <FilterAltIcon sx={iconSx} />}
      </Box>
    );
  };

  // Event handlers for apply filters view
  const handleFilterChange = (filterKey, newValue) => {
    // Handle last_contacted filter differently
    if (filterKey === 'last_contacted') {
      setLastContactedFilter(newValue);

      // If the value is empty or null, remove the filter
      if (!newValue) {
        setFilters(prev => {
          const newFilters = { ...prev };
          delete newFilters.last_contacted;
          return newFilters;
        });
        setHasChanges(true);
        return;
      }

      let timeFilter = {};
      const now = dayjs();

      switch (newValue) {
        case '1hour':
          timeFilter = {
            preset: '1hour',
            start_time: now.subtract(1, 'hour').utc().format('YYYY-MM-DD HH:mm'),
            end_time: now.format('YYYY-MM-DD HH:mm')
          };
          break;
        case '6hours':
          timeFilter = {
            preset: '6hours',
            start_time: now.subtract(6, 'hours').utc().format('YYYY-MM-DD HH:mm'),
            end_time: now.format('YYYY-MM-DD HH:mm')
          };
          break;
        case '12hours':
          timeFilter = {
            preset: '12hours',
            start_time: now.subtract(12, 'hours').utc().format('YYYY-MM-DD HH:mm'),
            end_time: now.format('YYYY-MM-DD HH:mm')
          };
          break;
        case '24hours':
          timeFilter = {
            preset: '24hours',
            start_time: now.subtract(24, 'hours').utc().format('YYYY-MM-DD HH:mm'),
            end_time: now.format('YYYY-MM-DD HH:mm')
          };
          break;
        case 'custom':
          if (customStartDateTime && customEndDateTime) {
            timeFilter = {
              preset: 'custom',
              start_time: customStartDateTime.utc().format('YYYY-MM-DD HH:mm'),
              end_time: customEndDateTime.format('YYYY-MM-DD HH:mm')
            };
          }
          break;
        default:
          // Clear the filter if none selected
          timeFilter = null;
      }

      // Only update if we have a valid time filter
      if (timeFilter) {
        // Update the filters object with the time filter
        setFilters(prev => ({
          ...prev,
          last_contacted: timeFilter
        }));
      } else {
        // Remove the filter if timeFilter is null
        setFilters(prev => {
          const newFilters = { ...prev };
          delete newFilters.last_contacted;
          return newFilters;
        });
      }

      // Update hasChanges flag
      setHasChanges(true);
      return;
    }

    // Special handling for array fields to ensure they're always arrays
    if (['status', 'lead_stage', 'tags', 'agent_id', 'source', 'bhk_type', 'lead_urgency', 'lead_lcp', 'occupation', 'site_visit_frequency', 'channel_partner', 'budget_ranges', 'locations', 'property_types', 'intent'].includes(filterKey)) {
      // Ensure we have an array
      const arrayValue = Array.isArray(newValue) ? newValue : (newValue ? [newValue] : []);

      setFilters(prev => ({
        ...prev,
        [filterKey]: arrayValue
      }));

      setHasChanges(true);
      return;
    }

    // Handle budget filter
    if (filterKey === 'budget') {
      // Ensure correct format with min_value and max_value
      const budgetValue = typeof newValue === 'object' ?
        {
          min_value: newValue.min_value !== undefined ? newValue.min_value : 0,
          max_value: newValue.max_value !== undefined ? newValue.max_value : 100000000
        } : newValue;

      setFilters(prev => ({
        ...prev,
        [filterKey]: budgetValue
      }));

      setHasChanges(true);
      return;
    }

    // Default handling for other filter types
    setFilters(prev => ({
      ...prev,
      [filterKey]: newValue
    }));

    setHasChanges(true);
  };

  const handleClearFilters = () => {
    // Clear local filter UI state
    setFilters({});
    setBudgetRange([2500000, 100000000]); // Reset budget range to 25L to 10Cr
    setLastContactedFilter(''); // Reset last contacted filter
    setCustomStartDateTime(null); // Reset custom start date/time
    setCustomEndDateTime(null); // Reset custom end date/time

    const clearedFilters = {};

    // Propagate cleared filters to list + context so default data is fetched
    if (onFiltersChange) {
      onFiltersChange(clearedFilters);
      setAppliedFilters(clearedFilters);
      setInitialFilters(clearedFilters);
    } else {
      setAppliedFilters(clearedFilters);
      setInitialFilters(clearedFilters);
      // Force refresh leads with no filters to get default data
      fetchLeads(clearedFilters, null, true);
    }

    // No pending changes after clear-and-apply
    setHasChanges(false);

    // Close the filter modal after clearing
    onClose();
  };

  const handleApplyFilters = () => {
    // Make a copy of the current filters
    const filtersToApply = { ...filters };

    // If lastContactedFilter is empty, make sure it's not in the applied filters
    if (!lastContactedFilter && filtersToApply.last_contacted) {
      delete filtersToApply.last_contacted;
    }

    // Check if budget filter has default values and remove it if so
    if (filtersToApply.budget) {
      const { min_value, max_value } = filtersToApply.budget;
      if ((min_value === 2500000 || min_value === 0 || min_value === undefined) &&
        (max_value === 100000000 || max_value === undefined)) {
        delete filtersToApply.budget;
      }
    }

    // Handle budget filter FIRST - ensure it's an object, not an array
    if (filtersToApply.budget) {
      if (Array.isArray(filtersToApply.budget) && filtersToApply.budget.length > 0) {
        // If it's an array, take the first object
        filtersToApply.budget = filtersToApply.budget[0];
      }
    }

    // Remove any empty array filters and convert objects to values for backend
    Object.keys(filtersToApply).forEach(key => {
      if (Array.isArray(filtersToApply[key])) {
        if (filtersToApply[key].length === 0) {
          delete filtersToApply[key];
        } else {
          // Convert objects to values for backend
          let values = filtersToApply[key].map(item => {
            if (typeof item === 'object' && item !== null && item.value !== undefined) {
              return item.value;
            }
            return item;
          });

          // For pipeline: when qualified is selected, always include follow_up (and vice versa)
          if (key === 'lead_stage') {
            const hasQualified = values.includes('qualified');
            const hasFollowUp = values.includes('follow_up');
            if (hasQualified && !hasFollowUp) values.push('follow_up');
            if (hasFollowUp && !hasQualified) values.push('qualified');
            values = Array.from(new Set(values));
          }

          filtersToApply[key] = values;
        }
      }
    });

    // Handle special field conversions between frontend and backend
    if (filtersToApply.agent_id && Array.isArray(filtersToApply.agent_id)) {
      // Ensure agent_id values are strings or numbers, not objects
      const agentIds = filtersToApply.agent_id.map(agent =>
        typeof agent === 'object' ? agent.id : agent
      );
      // Canonical backend key for assignments
      filtersToApply.assigned_to = agentIds;
      delete filtersToApply.agent_id;
    }

    // Convert channel_partner to channel_partners for backend
    if (filtersToApply.channel_partner && Array.isArray(filtersToApply.channel_partner)) {
      const channelPartnerIds = filtersToApply.channel_partner.map(cp =>
        typeof cp === 'object' ? cp.value : cp
      );
      filtersToApply.channel_partners = channelPartnerIds;
      delete filtersToApply.channel_partner;
    }

    // If onFiltersChange is provided (from LeadsV2), use it
    // This ensures page resets to 1
    if (onFiltersChange) {
      onFiltersChange(filtersToApply);
      // Also persist into context so the next time the popover opens,
      // it can hydrate from the last applied filters
      setAppliedFilters(filtersToApply);
      setInitialFilters(filtersToApply);
      setHasChanges(false);
    } else {
      // Fallback for other pages using this component
      // Update applied filters in context
      setAppliedFilters(filtersToApply);
      setInitialFilters(filtersToApply);
      setHasChanges(false);
      // Call the API to fetch filtered leads with forceRefresh to bypass duplicate prevention
      fetchLeads(filtersToApply, null, true);
    }

    // Close the filter modal
    onClose();
  };

  const handleOpenSaveDialog = () => {
    if (!hasMeaningfulFilters(filters)) {
      openErrorModal?.('Error', 'Please apply at least one filter before saving');
      return;
    }
    onOpenSaveSearch?.(filters);
  };

  // Event handlers for saved searches view
  const handleExpandSearch = (searchId) => {
    const search = statusCounts.saved_searches.find(search => search.id === searchId);

    if (expandedSearchId === searchId) {
      // If already expanded, collapse and cancel edit mode
      setExpandedSearchId(null);
      setEditingSearchId(null);
      setEditingName('');
      setEditingFilters({});
    } else {
      // First close any other expanded item
      if (expandedSearchId !== null) {
        // Cancel edit mode for previously expanded item
        setEditingSearchId(null);
        setEditingName('');
        setEditingFilters({});
      }

      // When expanding a search, automatically enter edit mode
      setExpandedSearchId(searchId);
      setEditingSearchId(searchId);

      if (search) {
        setEditingName(search.name || '');

        // Make a copy of search criteria to modify if needed
        const searchCriteria = { ...search.search_criteria } || {};

        // If the search has agents but no agent_id, convert it for UI
        if (searchCriteria.agents && !searchCriteria.agent_id) {
          searchCriteria.agent_id = searchCriteria.agents;
          delete searchCriteria.agents;
        }
        // If the search has assigned_to (canonical) but no agent_id, convert it for UI
        if (searchCriteria.assigned_to && !searchCriteria.agent_id) {
          searchCriteria.agent_id = searchCriteria.assigned_to;
          delete searchCriteria.assigned_to;
        }

        // If the search has channel_partners but no channel_partner, convert it for UI
        if (searchCriteria.channel_partners && !searchCriteria.channel_partner) {
          searchCriteria.channel_partner = searchCriteria.channel_partners;
          delete searchCriteria.channel_partners;
        }

        // Legacy: convert old budget slider {min_value, max_value} to budget_ranges if no budget_ranges present
        if (searchCriteria.budget && !searchCriteria.budget_ranges) {
          delete searchCriteria.budget;
        }

        // Convert values back to objects for UI display (only for editing, not for applying)
        Object.keys(searchCriteria).forEach(key => {
          if (Array.isArray(searchCriteria[key]) && defaultFilters[key]) {
            const filterOptions = defaultFilters[key];
            if (Array.isArray(filterOptions)) {
              searchCriteria[key] = searchCriteria[key].map(value => {
                const option = filterOptions.find(opt => 
                  (opt.value || opt) === value
                );
                return option || value;
              });
            }
          }
        });

        setEditingFilters(searchCriteria);

        // Set budget range if it exists in the search criteria
        if (searchCriteria.budget) {
          const { min_value, max_value } = searchCriteria.budget;
          setBudgetRange([
            min_value || 2500000,
            max_value || 100000000
          ]);
        } else {
          setBudgetRange([2500000, 100000000]); // Reset to default
        }
      }
    }
  };

  const handleEditSearch = (search) => {
    setEditingSearchId(search.id);
    setEditingName(search.name || '');

    // Make a copy of search criteria to modify if needed
    const searchCriteria = { ...search.search_criteria } || {};

    // If the search has agents but no agent_id, convert it for UI
    if (searchCriteria.agents && !searchCriteria.agent_id) {
      searchCriteria.agent_id = searchCriteria.agents;
      delete searchCriteria.agents;
    }
    // If the search has assigned_to (canonical) but no agent_id, convert it for UI
    if (searchCriteria.assigned_to && !searchCriteria.agent_id) {
      searchCriteria.agent_id = searchCriteria.assigned_to;
      delete searchCriteria.assigned_to;
    }

    // If the search has channel_partners but no channel_partner, convert it for UI
    if (searchCriteria.channel_partners && !searchCriteria.channel_partner) {
      searchCriteria.channel_partner = searchCriteria.channel_partners;
      delete searchCriteria.channel_partners;
    }

    // Convert values back to objects for UI display (only for editing, not for applying)
    Object.keys(searchCriteria).forEach(key => {
      if (Array.isArray(searchCriteria[key]) && defaultFilters[key]) {
        const filterOptions = defaultFilters[key];
        if (Array.isArray(filterOptions)) {
          // Convert values to objects with label/value structure for UI display
          searchCriteria[key] = searchCriteria[key].map(value => {
            const option = filterOptions.find(opt => 
              (opt.value || opt) === value
            );
            return option || value;
          });
        }
      }
    });

    setEditingFilters(searchCriteria);

    // Set budget range if it exists in the search criteria
    if (searchCriteria.budget) {
      const { min_value, max_value } = searchCriteria.budget;
      setBudgetRange([
        min_value || 2500000,
        max_value || 100000000 // Changed to 10Cr
      ]);
    } else {
      setBudgetRange([2500000, 100000000]); // Reset to default, changed to 10Cr
    }
  };

  const handleCancelEdit = () => {
    setEditingSearchId(null);
    setEditingName('');
    setEditingFilters({});
  };

  const handleUpdateSearch = async () => {
    if (!editingName.trim()) {
      openErrorModal?.('Error', 'Please enter a search name');
      return;
    }

    // Validate filters before updating
    if (Object.keys(editingFilters).length === 0) {
      openErrorModal?.('Error', 'At least one filter must be applied');
      return;
    }

    // Check filters for empty values
    for (const [key, value] of Object.entries(editingFilters)) {
      if (Array.isArray(value) && value.length === 0) {
        openErrorModal?.('Error', `${toTitleCase(key)} filter cannot be empty`);
        return;
      }
    }

    try {
      setUpdatingSearch(true);

      // Create a copy of editingFilters for the search criteria
      const searchCriteria = { ...editingFilters };

      // Convert objects to values for backend
      Object.keys(searchCriteria).forEach(key => {
        if (Array.isArray(searchCriteria[key])) {
          searchCriteria[key] = searchCriteria[key].map(item => {
            if (typeof item === 'object' && item !== null && item.value !== undefined) {
              return item.value;
            }
            return item;
          });
        }
      });

      // Handle budget filter specifically - ensure it's in the correct format
      if (searchCriteria.budget) {
        // If budget is an array (from budgetRange state), convert to object
        if (Array.isArray(searchCriteria.budget)) {
          searchCriteria.budget = {
            min_value: searchCriteria.budget[0],
            max_value: searchCriteria.budget[1]
          };
        }
        // If budget is already an object but has wrong keys, fix them
        else if (searchCriteria.budget.min !== undefined || searchCriteria.budget.max !== undefined) {
          searchCriteria.budget = {
            min_value: searchCriteria.budget.min || searchCriteria.budget.min_value,
            max_value: searchCriteria.budget.max || searchCriteria.budget.max_value
          };
        }
        // Ensure budget is not an array of objects
        else if (Array.isArray(searchCriteria.budget) && searchCriteria.budget.length > 0 && typeof searchCriteria.budget[0] === 'object') {
          // If it's an array of objects, take the first object
          searchCriteria.budget = searchCriteria.budget[0];
        }
      }

      const minBUp = searchCriteria.budget?.min_value ?? searchCriteria.budget?.min;
      const maxBUp = searchCriteria.budget?.max_value ?? searchCriteria.budget?.max;
      if (
        searchCriteria.budget_ranges?.length > 0 ||
        (searchCriteria.budget && minBUp === 0 && maxBUp === 0)
      ) {
        delete searchCriteria.budget;
      }

      // Convert agent filter to canonical key for bookmark update
      if (searchCriteria.agent_id && Array.isArray(searchCriteria.agent_id)) {
        const agentIds = searchCriteria.agent_id.map((a) => (typeof a === 'object' ? a.id : a));
        searchCriteria.assigned_to = agentIds;
        delete searchCriteria.agent_id;
      }
      if (searchCriteria.agents) delete searchCriteria.agents;

      const updatedSearch = {
        id: editingSearchId,
        name: editingName,
        search_type: 'lead',
        search_criteria: searchCriteria
      };

      const updated_obj = await updateSavedSearch(editingSearchId, updatedSearch).then((data) => {
        return data;
      });
      statusCounts.saved_searches.map(search => {
        if (search.id === updated_obj.id) {
          search.name = updated_obj.name;
          search.search_criteria = updated_obj.search_criteria;
        }
      });
      setStatusCounts(statusCounts);
      openSuccessModal?.('Success', 'Search updated successfully');

      // Reset all editing and expanded states to ensure buttons aren't shown
      setExpandedSearchId(null);
      setEditingSearchId(null);
      setEditingName('');
      setEditingFilters({});
    } catch (error) {
      console.error('Error updating search:', error);

      // Check for validation errors from the backend
      if (error.response && error.response.data) {
        // Format the error message for the modal
        const errorMessage = Object.entries(error.response.data)
          .map(([key, value]) => `${key}: ${value}`)
          .join('\n');

        openErrorModal?.('Error', errorMessage || 'Failed to update search');
      } else {
        openErrorModal?.('Error', 'Failed to update search');
      }
    } finally {
      setUpdatingSearch(false);
    }
  };

  const handleDeleteSearch = async (id) => {
    try {
      await deleteSavedSearch(id);
      statusCounts.saved_searches = statusCounts.saved_searches.filter(search => search.id !== id);
      setStatusCounts(statusCounts);
      openSuccessModal?.('Success', 'Search deleted successfully');

      if (editingSearchId === id) {
        setEditingSearchId(null);
        setEditingName('');
        setEditingFilters({});
      }

      if (expandedSearchId === id) {
        setExpandedSearchId(null);
      }
    } catch (error) {
      openErrorModal?.('Error', 'Failed to delete search');
      console.error('Error deleting search:', error);
    }
  };

  // Then update handleApplySavedSearch
  const handleApplySavedSearch = (search) => {
    if (search && search.search_criteria) {
      // Keep the search criteria as-is for backend (don't convert to objects)
      // The backend expects simple values, not objects with value/label structure
      const searchCriteria = { ...search.search_criteria };
      
      // Ensure all array values are simple strings/values, not objects
      Object.keys(searchCriteria).forEach(key => {
        if (Array.isArray(searchCriteria[key])) {
          searchCriteria[key] = searchCriteria[key].map(item => {
            // If it's an object with value property, extract the value
            if (typeof item === 'object' && item !== null && item.value !== undefined) {
              return item.value;
            }
            // Otherwise keep as-is
            return item;
          });
        }
      });

      // Create a modified search object with clean criteria
      const modifiedSearch = {
        ...search,
        search_criteria: searchCriteria
      };

      // Call the API to apply the saved search
      applySavedSearch(modifiedSearch);

      // Close the filter popover
      onClose();
    }
  };

  // Editing saved search filters
  const handleEditFilterChange = (filterKey, newValue) => {
    setEditingFilters(prev => ({
      ...prev,
      [filterKey]: newValue
    }));
  };

  const handleRemoveFilter = (filterKey) => {
    setEditingFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[filterKey];
      return newFilters;
    });
  };

  const handleAddFilterClick = (event) => {
    setAddFilterAnchorEl(event.currentTarget);
  };

  const handleAddFilterClose = () => {
    setAddFilterAnchorEl(null);
  };

  const handleAddFilter = (filterKey) => {
    let defaultValue;

    if (filterKey === 'budget') {
      defaultValue = { min_value: 0, max_value: 100000000 };
    } else if (filterKey === 'agent_id') {
      defaultValue = []; // Empty array for agent IDs
    } else {
      defaultValue = [];
    }

    setEditingFilters(prev => ({
      ...prev,
      [filterKey]: defaultValue
    }));

    handleAddFilterClose();
  };

  // Get available filters to add (ones not already in the saved search)
  const getAvailableFilters = () => {
    if (!defaultFilters) return [];

    const currentFilters = Object.keys(editingFilters);
    const standardFilters = Object.keys(defaultFilters).filter(key => !currentFilters.includes(key));

    // Add agent_id if neither agent_id nor agents is in the filters
    if (!currentFilters.includes('agent_id') && !currentFilters.includes('agents')) {
      return [...standardFilters, 'agent_id'];
    }

    return standardFilters;
  };

  // Add a handler for budget slider change
  const handleBudgetRangeChange = (event, newValue) => {
    setBudgetRange(newValue);
    // Update filters with the min and max values
    setFilters(prev => ({
      ...prev,
      budget: {
        min_value: newValue[0],
        max_value: newValue[1]
      }
    }));
  };

  // Also, set up an edit-specific budget handler
  const handleEditBudgetRangeChange = (event, newValue) => {
    setBudgetRange(newValue);
    // Update editing filters with the min and max values
    setEditingFilters(prev => ({
      ...prev,
      budget: {
        min_value: newValue[0],
        max_value: newValue[1]
      }
    }));
  };

  // Add this helper function for formatting currency values
  const formatCurrency = (value) => {
    if (value >= 10000000) {
      return `₹${(value / 10000000).toFixed(1)}Cr`;
    } else if (value >= 100000) {
      return `₹${(value / 100000).toFixed(1)}L`;
    } else if (value >= 1000) {
      return `₹${(value / 1000).toFixed(1)}K`;
    }
    return `₹${value}`;
  };
  // Add a new JSX block for the Last Contacted filter
  const renderLastContactedFilter = () => (
    <Box
      key="last_contacted"
      sx={{
        px: 1.5,
        py: 1.25,
        borderBottom: `1px solid ${T.borderL}`,
        '&:last-child': { borderBottom: 'none' },
        position: 'relative',
        transition: 'background-color 0.15s',
        '&:hover': { bgcolor: 'rgba(10,107,62,0.02)' },
      }}
    >
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        mb: 1,
        justifyContent: 'space-between',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {getFilterIcon('last_contacted')}
          <Typography sx={{ ...sectionHeaderStyles, color: T.ink2 }}>
            Last Contacted
          </Typography>
        </Box>
        {lastContactedFilter && (
          <Tooltip title="Clear filter">
            <IconButton
              size="small"
              sx={{ p: 0.5 }}
              onClick={() => {
                setLastContactedFilter('');
                const newFilters = { ...filters };
                delete newFilters.last_contacted;
                setFilters(newFilters);
                setHasChanges(true);
                setCustomStartDateTime(null);
                setCustomEndDateTime(null);
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Child options: indented under section header */}
      <FormControl component="fieldset" sx={{ width: '100%', pl: 2.5 }}>
        <RadioGroup
          value={lastContactedFilter}
          onChange={(e) => {
            const value = e.target.value;
            setLastContactedFilter(value);

            if (value === '') {
              const newFilters = { ...filters };
              delete newFilters.last_contacted;
              setFilters(newFilters);
            } else {
              handleFilterChange('last_contacted', value);
            }
          }}
        >
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <FormControlLabel
                value="1hour"
                control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#06B6D4' }, padding: '4px' }} />}
                label={<Typography variant="body2" fontSize="0.7rem">Last 1 hours</Typography>}
                sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.7rem', fontWeight: lastContactedFilter === '1hour' ? 600 : 400 } }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                value="6hours"
                control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#06B6D4' }, padding: '4px' }} />}
                label={<Typography variant="body2" fontSize="0.7rem">Last 6 hours</Typography>}
                sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.7rem', fontWeight: lastContactedFilter === '6hours' ? 600 : 400 } }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                value="12hours"
                control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#06B6D4' }, padding: '4px' }} />}
                label={<Typography variant="body2" fontSize="0.7rem">Last 12 hours</Typography>}
                sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.7rem', fontWeight: lastContactedFilter === '12hours' ? 600 : 400 } }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                value="24hours"
                control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#06B6D4' }, padding: '4px' }} />}
                label={<Typography variant="body2" fontSize="0.7rem">Last 24 hours</Typography>}
                sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.7rem', fontWeight: lastContactedFilter === '24hours' ? 600 : 400 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                value="custom"
                control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#06B6D4' }, padding: '4px' }} />}
                label={<Typography variant="body2" fontSize="0.7rem">Custom Date/Time Range</Typography>}
                sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.7rem', fontWeight: lastContactedFilter === 'custom' ? 600 : 400 } }}
              />
            </Grid>
          </Grid>
        </RadioGroup>
      </FormControl>

      {lastContactedFilter === 'custom' && (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={1.25} sx={{ mt: 1.5, pl: 2.5, pr: 0.5 }}>
            {[
              { label: 'Start', value: customStartDateTime, isStart: true, min: null },
              { label: 'End', value: customEndDateTime, isStart: false, min: customStartDateTime }
            ].map(({ label, value, isStart, min }) => (
              <Grid item xs={12} sm={6} key={label}>
                <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: T.muted, mb: 0.4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {label}
                </Typography>
                <DateTimePicker
                  value={value}
                  onChange={(date) => handleCustomDateTimeChange(date, isStart)}
                  format="DD/MM/YY hh:mm a"
                  ampm={true}
                  {...(min ? { minDateTime: min } : {})}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      placeholder: 'DD/MM/YY HH:mm',
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          fontSize: '0.72rem',
                          fontWeight: 500,
                          borderRadius: '8px',
                          bgcolor: T.bg,
                          transition: 'all 0.15s ease',
                          '& fieldset': { borderColor: T.border },
                          '&:hover fieldset': { borderColor: T.green },
                          '&.Mui-focused fieldset': { borderColor: T.green, borderWidth: '1.5px' },
                          '&.Mui-focused': { boxShadow: `0 0 0 3px ${T.greenSf}` },
                        },
                        '& .MuiInputAdornment-root .MuiIconButton-root': {
                          color: T.green,
                          padding: '4px',
                          '&:hover': { bgcolor: T.greenSf }
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '0.72rem',
                          fontWeight: 500,
                          py: '6px',
                          color: value ? T.ink : T.muted,
                        },
                      }
                    },
                    popper: {
                      sx: {
                        '& .MuiPaper-root': {
                          borderRadius: '12px',
                          border: `1px solid ${T.border}`,
                          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                          overflow: 'hidden',
                        },
                        '& .MuiPickersDay-root': {
                          borderRadius: '6px',
                          fontSize: '0.75rem',
                          '&.Mui-selected': { bgcolor: T.green, '&:hover': { bgcolor: T.greenMd } },
                          '&:hover': { bgcolor: T.greenSf },
                        },
                        '& .MuiPickersDay-today': {
                          border: `1.5px solid ${T.green}`,
                          color: T.green,
                        },
                        '& .MuiMultiSectionDigitalClock-root': {
                          borderLeft: `1px solid ${T.borderL}`,
                        },
                        '& .MuiMultiSectionDigitalClockSection-item': {
                          borderRadius: '6px',
                          fontSize: '0.75rem',
                          '&.Mui-selected': { bgcolor: T.green, color: '#fff', '&:hover': { bgcolor: T.greenMd } },
                        },
                        '& .MuiDialogActions-root .MuiButton-root': {
                          color: T.green,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                        },
                        '& .MuiPickersCalendarHeader-label': {
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          color: T.ink,
                        },
                        '& .MuiPickersArrowSwitcher-button': {
                          color: T.ink2,
                          '&:hover': { bgcolor: T.greenSf },
                        },
                        '& .MuiDayCalendar-weekDayLabel': {
                          fontSize: '0.68rem',
                          color: T.muted,
                          fontWeight: 600,
                        },
                      }
                    }
                  }}
                />
                </Box>
              </Grid>
            ))}
          </Grid>
        </LocalizationProvider>
      )}
    </Box>
  );

  // Handle custom date-time changes
  const handleCustomDateTimeChange = (date, isStart) => {
    if (isStart) {
      setCustomStartDateTime(date);
    } else {
      setCustomEndDateTime(date);
    }

    // Only update the filter if both start and end are set
    if ((isStart && customEndDateTime) || (!isStart && customStartDateTime)) {
      const start = isStart ? date : customStartDateTime;
      const end = isStart ? customEndDateTime : date;

      if (start && end) {
        setFilters(prev => ({
          ...prev,
          last_contacted: {
            preset: 'custom',
            start_time: start.utc().format('YYYY-MM-DD HH:mm'),
            end_time: end.utc().format('YYYY-MM-DD HH:mm')
          }
        }));

        // Update hasChanges flag
        setHasChanges(true);
      }
    }
  };

  // Visit Frequency — multi-select toggles (clear selected vs plain chips)
  const renderVisitFrequencyFilter = () => {
    const vfPalette = FILTER_ICON_COLORS.site_visit_frequency;
    const vfOptions = defaultFilters.site_visit_frequency || [];
    const selectedRaw = filters.site_visit_frequency || [];
    const selectedValues = selectedRaw.map((x) => (typeof x === 'object' && x !== null ? x.value : x));

    const handleVisitFrequencyToggle = (_e, next) => {
      if (next == null) return;
      const nextObjects = vfOptions.filter((opt) => next.includes(opt.value ?? opt));
      handleFilterChange('site_visit_frequency', nextObjects);
    };

    return (
      <Box
        key="site_visit_frequency"
        sx={{
          px: 1.5,
          py: 1.25,
          borderBottom: `1px solid ${T.borderL}`,
          '&:last-child': { borderBottom: 'none' },
          position: 'relative',
          transition: 'background-color 0.15s',
          '&:hover': { bgcolor: 'rgba(10,107,62,0.02)' },
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 0.75,
          justifyContent: 'space-between',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {getFilterIcon('site_visit_frequency')}
            <Typography sx={{ ...sectionHeaderStyles, color: T.ink2 }}>
              Visit Frequency
            </Typography>
          </Box>
          {selectedRaw.length > 0 && (
            <Tooltip title="Clear filter">
              <IconButton
                size="small"
                sx={{ p: 0.5 }}
                onClick={() => {
                  const newFilters = { ...filters };
                  delete newFilters.site_visit_frequency;
                  setFilters(newFilters);
                  setHasChanges(true);
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {vfOptions.length > 0 && (
          <ToggleButtonGroup
            value={selectedValues}
            onChange={handleVisitFrequencyToggle}
            multiple
            size="small"
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.75,
              '& .MuiToggleButtonGroup-grouped': {
                margin: '0 !important',
                border: `1px solid ${T.borderL} !important`,
                borderRadius: '8px !important',
              },
            }}
          >
            {vfOptions.map((option) => {
              const val = option.value ?? option;
              return (
                <ToggleButton
                  key={val}
                  value={val}
                  disableRipple
                  sx={{
                    px: 1.15,
                    py: 0.4,
                    fontSize: '0.72rem',
                    fontWeight: 500,
                    lineHeight: 1.3,
                    textTransform: 'none',
                    color: T.ink2,
                    bgcolor: T.white,
                    transition: 'background-color 0.15s, border-color 0.15s, color 0.15s',
                    '&:hover': {
                      bgcolor: T.borderL,
                      borderColor: `${vfPalette.color}40 !important`,
                    },
                    '&.Mui-selected': {
                      bgcolor: `${vfPalette.bg} !important`,
                      color: `${vfPalette.color} !important`,
                      borderColor: `${vfPalette.color}44 !important`,
                      fontWeight: 600,
                      '&:hover': {
                        bgcolor: `${vfPalette.bg} !important`,
                      },
                    },
                  }}
                >
                  {option.label ?? String(option)}
                </ToggleButton>
              );
            })}
          </ToggleButtonGroup>
        )}
      </Box>
    );
  };

  // Add a new function to render the Assigned Agent filter
  const renderAssignedAgentFilter = () => (
    <Box
      key="agent_id"
      sx={{
        px: 1.5,
        py: 1.25,
        borderBottom: `1px solid ${T.borderL}`,
        '&:last-child': { borderBottom: 'none' },
        position: 'relative',
        transition: 'background-color 0.15s',
        '&:hover': { bgcolor: 'rgba(10,107,62,0.02)' },
      }}
    >
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        mb: 0.75,
        justifyContent: 'space-between',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {getFilterIcon('agent_id')}
          <Typography sx={{ ...sectionHeaderStyles, color: T.ink2 }}>
            Assigned Agent
          </Typography>
        </Box>
        {filters.agent_id && (
          <Tooltip title="Clear filter">
            <IconButton
              size="small"
              sx={{ p: 0.5 }}
              onClick={() => {
                const newFilters = { ...filters };
                delete newFilters.agent_id;
                setFilters(newFilters);
                setHasChanges(true);
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <AgentSelect
        agents={agents || []}
        value={filters.agent_id || []}
        onChange={(agentIds) => handleFilterChange('agent_id', agentIds)}
        label="Select Agents"
        multiple={true}
        size="small"
        themeVariant="lead"
        sx={{
          mt: 0.75,
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            fontSize: '0.72rem',
            minHeight: '30px',
            transition: 'box-shadow 0.15s, border-color 0.15s',
            '&.Mui-focused': {
              boxShadow: '0 0 0 3px rgba(139,92,246,0.12)',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#8B5CF6' },
            },
          },
        }}
      />
    </Box>
  );

  // Loading state
  if (loading) {
    return (
      <Popover open={open} anchorEl={anchorEl} onClose={onClose} PaperProps={{ sx: { width: 320, p: 3, textAlign: 'center', borderRadius: '14px' } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: '12px',
            background: `linear-gradient(135deg, ${T.greenSf} 0%, ${T.greenLt} 100%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'pulse 1.5s ease-in-out infinite',
            '@keyframes pulse': { '0%, 100%': { opacity: 0.6 }, '50%': { opacity: 1 } },
          }}>
            <FilterAltIcon sx={{ fontSize: 20, color: T.green }} />
          </Box>
          <Typography sx={{ fontSize: '0.78rem', color: T.muted, fontWeight: 500 }}>Loading filters...</Typography>
        </Box>
      </Popover>
    );
  }

  // Render both views side by side
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 660,
          maxHeight: '88vh',
          p: 0,
          borderRadius: '14px',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)',
          border: `1px solid ${T.border}`,
        },
      }}
    >
      <Grid container>
        {/* Apply Filters Panel */}
        <Grid item xs={6} sx={{
          borderRight: `1px solid ${T.border}`,
          display: 'flex',
          flexDirection: 'column',
          height: '76vh',
          bgcolor: T.white,
        }}>
          {/* Header */}
          <Box sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${T.border}`,
            background: `linear-gradient(135deg, ${T.bg} 0%, #FFFFFF 100%)`,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: '10px',
                background: `linear-gradient(135deg, ${T.green} 0%, ${T.greenMd} 100%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(10,107,62,0.25)',
              }}>
                <FilterAltIcon sx={{ fontSize: 16, color: '#fff' }} />
              </Box>
              <Box>
                <Typography fontWeight={700} color={T.ink} sx={{ fontSize: '0.85rem', lineHeight: 1.2 }}>
                  Filters
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', color: T.muted, lineHeight: 1.2 }}>
                  {Object.keys(filters).filter(k => {
                    const v = filters[k];
                    return Array.isArray(v) ? v.length > 0 : v != null;
                  }).length} active
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={onClose} sx={{ p: 0.5, color: T.muted, borderRadius: '8px', '&:hover': { color: T.ink2, bgcolor: T.borderL } }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          {/* Filters List - Show ALL filters by default */}
          <Box sx={{ overflow: 'auto', flex: 1 }}>
            {defaultFilters && Object.keys(defaultFilters).length > 0 ? (
              <>
                {/* First render the Last Contacted filter separately */}
                {/* Render filters in the specified order */}
                {filterOrder
                  .map(filterKey => {
                    // agent_id comes from the `agents` prop, not defaultFilters
                    if (filterKey === 'agent_id') {
                      return renderAssignedAgentFilter();
                    }

                    if (!defaultFilters[filterKey]) return null;

                    const icon = getFilterIcon(filterKey);
                    const label = FILTER_LABELS[filterKey] || toTitleCase(filterKey);
                    const currentValue = filters[filterKey] || [];

                    if (filterKey === 'site_visit_frequency') {
                      return renderVisitFrequencyFilter();
                    }

                    return (
                      <Box
                        key={filterKey}
                        sx={{
                          px: 1.5,
                          py: 1.25,
                          borderBottom: `1px solid ${T.borderL}`,
                          '&:last-child': { borderBottom: 'none' },
                          position: 'relative',
                          transition: 'background-color 0.15s',
                          '&:hover': { bgcolor: 'rgba(10,107,62,0.02)' },
                        }}
                      >
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 0.75,
                          justifyContent: 'space-between',
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            {icon}
                            <Typography sx={{ ...sectionHeaderStyles, color: T.ink2 }}>
                              {label}
                            </Typography>
                          </Box>

                          {/* Show clear button if filter is active */}
                          {filters[filterKey] && (
                            <Tooltip title="Clear filter">
                              <IconButton
                                size="small"
                                sx={{ p: 0.5 }}
                                onClick={() => {
                                  const newFilters = { ...filters };
                                  delete newFilters[filterKey];
                                  setFilters(newFilters);
                                  setHasChanges(true);

                                  // Reset budget range if clearing budget filter
                                  if (filterKey === 'budget') {
                                    setBudgetRange([2500000, 100000000]);
                                  }
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>

                        {filterKey === 'budget' ? (
                          <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75, px: 0.5 }}>
                              <Box sx={{ px: 1, py: 0.25, borderRadius: '6px', bgcolor: '#E5F5EC', border: '1px solid #0A6B3E22' }}>
                                <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: T.green }}>
                                  {formatCurrency(budgetRange[0])}
                                </Typography>
                              </Box>
                              <Box sx={{ px: 1, py: 0.25, borderRadius: '6px', bgcolor: '#E5F5EC', border: '1px solid #0A6B3E22' }}>
                                <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: T.green }}>
                                  {formatCurrency(budgetRange[1])}
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ px: 1.5, py: 0.5 }}>
                              <Slider
                                value={budgetRange}
                                onChange={handleBudgetRangeChange}
                                valueLabelDisplay="off"
                                min={BUDGET_SLIDER_MIN}
                                max={BUDGET_SLIDER_MAX}
                                step={BUDGET_SLIDER_STEP}
                                sx={{
                                  color: T.green,
                                  height: 3,
                                  '& .MuiSlider-thumb': {
                                    height: 14,
                                    width: 14,
                                    bgcolor: '#fff',
                                    border: `2px solid ${T.green}`,
                                    boxShadow: '0 1px 3px rgba(10,107,62,0.2)',
                                    '&:hover, &.Mui-focusVisible': { boxShadow: `0 0 0 5px ${T.greenSf}` },
                                  },
                                  '& .MuiSlider-track': {
                                    background: `linear-gradient(90deg, ${T.green} 0%, ${T.greenMd} 100%)`,
                                    border: 'none',
                                    height: 3,
                                  },
                                  '& .MuiSlider-rail': {
                                    bgcolor: T.borderL,
                                    opacity: 1,
                                    height: 3,
                                  },
                                }}
                              />
                            </Box>
                          </Box>
                        ) : Array.isArray(defaultFilters[filterKey]) && defaultFilters[filterKey].length > 0 ? (
                          <Autocomplete
                            multiple
                            size="small"
                            options={
                              filterKey === 'lead_stage'
                                ? defaultFilters[filterKey].filter(
                                    (opt) => (opt.value || opt) !== 'follow_up'
                                  )
                                : defaultFilters[filterKey]
                            }
                            value={Array.isArray(currentValue) ? currentValue : []}
                            onChange={(e, newValue) => handleFilterChange(filterKey, newValue)}
                            getOptionLabel={(option) => option.label || option}
                            getOptionValue={(option) => option.value || option}
                            isOptionEqualToValue={(option, value) =>
                              (option.value || option) === (value.value || value)
                            }
                            slotProps={filterAutocompleteSlotProps}
                            sx={{
                              ...autocompleteStyles,
                              mt: 0.75,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                transition: 'box-shadow 0.15s, border-color 0.15s',
                                '&.Mui-focused': {
                                  boxShadow: `0 0 0 3px ${(FILTER_ICON_COLORS[filterKey]?.color || T.green)}15`,
                                  '& .MuiOutlinedInput-notchedOutline': { borderColor: FILTER_ICON_COLORS[filterKey]?.color || T.green },
                                },
                              },
                            }}
                            renderInput={(params) => (
                              <TextField {...params} placeholder={`Search ${label}`} variant="outlined" size="small" fullWidth />
                            )}
                            renderTags={(value, getTagProps) => {
                              const palette = FILTER_ICON_COLORS[filterKey] || { color: T.green, bg: T.greenSf };
                              return value.map((option, index) => (
                                <Chip
                                  {...getTagProps({ index })}
                                  label={option.label || option}
                                  size="small"
                                  sx={{
                                    ...chipStyles,
                                    backgroundColor: palette.bg,
                                    color: palette.color,
                                    border: `1px solid ${palette.color}22`,
                                    fontWeight: 500,
                                    '&:hover': { backgroundColor: `${palette.color}18` },
                                    '& .MuiChip-deleteIcon': { color: palette.color, opacity: 0.5, '&:hover': { opacity: 1 } },
                                  }}
                                />
                              ));
                            }}
                          />
                        ) : typeof defaultFilters[filterKey] === 'object' && defaultFilters[filterKey] !== null ? (
                          <Autocomplete
                            multiple
                            size="small"
                            options={Object.keys(defaultFilters[filterKey])}
                            value={Array.isArray(currentValue) ? currentValue : []}
                            onChange={(e, newValue) => handleFilterChange(filterKey, newValue)}
                            getOptionLabel={(option) => defaultFilters[filterKey][option] || toTitleCase(option)}
                            slotProps={filterAutocompleteSlotProps}
                            sx={{
                              ...autocompleteStyles,
                              mt: 0.75,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                transition: 'box-shadow 0.15s, border-color 0.15s',
                                '&.Mui-focused': {
                                  boxShadow: `0 0 0 3px ${(FILTER_ICON_COLORS[filterKey]?.color || T.green)}15`,
                                  '& .MuiOutlinedInput-notchedOutline': { borderColor: FILTER_ICON_COLORS[filterKey]?.color || T.green },
                                },
                              },
                            }}
                            renderInput={(params) => (
                              <TextField {...params} placeholder={`Select ${label}`} variant="outlined" size="small" fullWidth />
                            )}
                            renderTags={(value, getTagProps) => {
                              const palette = FILTER_ICON_COLORS[filterKey] || { color: T.green, bg: T.greenSf };
                              return value.map((option, index) => (
                                <Chip
                                  {...getTagProps({ index })}
                                  label={defaultFilters[filterKey][option] || toTitleCase(option)}
                                  size="small"
                                  sx={{
                                    ...chipStyles,
                                    backgroundColor: palette.bg,
                                    color: palette.color,
                                    border: `1px solid ${palette.color}22`,
                                    fontWeight: 500,
                                    '& .MuiChip-deleteIcon': { color: palette.color, opacity: 0.5, '&:hover': { opacity: 1 } },
                                  }}
                                />
                              ));
                            }}
                          />
                        ) : null}
                      </Box>
                    );
                  })}

              </>
            ) : (
              <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
                <Box sx={{
                  width: 48, height: 48, borderRadius: '14px',
                  background: `linear-gradient(135deg, ${T.greenSf} 0%, ${T.greenLt} 100%)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5,
                }}>
                  <FilterAltIcon sx={{ fontSize: 24, color: T.green }} />
                </Box>
                <Typography sx={{ mb: 0.5, fontSize: '0.82rem', fontWeight: 600, color: T.ink2 }}>No filters available</Typography>
                <Button variant="outlined" size="small" sx={{ mt: 1, fontSize: '0.72rem', minHeight: 30, borderRadius: '8px', textTransform: 'none', borderColor: T.border, color: T.ink2 }}>
                  Refresh
                </Button>
              </Box>
            )}
          </Box>

          {/* Footer Buttons */}
          <Box sx={{
            p: 1.25,
            borderTop: `1px solid ${T.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: `linear-gradient(135deg, ${T.bg} 0%, #FFFFFF 100%)`,
          }}>
            {onOpenSaveSearch ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<BookmarkBorderIcon sx={{ fontSize: 14 }} />}
                onClick={handleOpenSaveDialog}
                disabled={!hasMeaningfulFilters(filters)}
                sx={{
                  borderRadius: '8px', fontWeight: 600, fontSize: '0.72rem', py: 0.5, minHeight: 30,
                  borderColor: T.border, color: T.ink2, textTransform: 'none',
                  '&:hover': { borderColor: T.subtle, bgcolor: T.borderL },
                  '&.Mui-disabled': { borderColor: T.borderL, color: T.muted, opacity: 1 },
                }}
              >
                Save
              </Button>
            ) : (
              <Box sx={{ minWidth: 56 }} />
            )}
            <Box sx={{ display: 'flex', gap: 0.75 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={handleClearFilters}
                disabled={!hasMeaningfulFilters(filters)}
                sx={{
                  borderRadius: '8px', fontWeight: 600, fontSize: '0.72rem', py: 0.5, minHeight: 30,
                  borderColor: T.border, color: T.ink2, textTransform: 'none',
                  '&:hover': { borderColor: '#EF4444', color: '#EF4444', bgcolor: '#FEF2F2' },
                  '&.Mui-disabled': { borderColor: T.borderL, color: T.muted, opacity: 1 },
                }}
              >
                Clear all
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleApplyFilters}
                disabled={!hasMeaningfulFilters(filters) || !hasChanges}
                sx={{
                  borderRadius: '8px',
                  fontWeight: 600,
                  fontSize: '0.72rem',
                  py: 0.5,
                  px: 2.5,
                  minHeight: 30,
                  textTransform: 'none',
                  color: '#fff',
                  background: `linear-gradient(135deg, ${T.green} 0%, ${T.greenMd} 100%)`,
                  boxShadow: '0 2px 10px rgba(10,107,62,0.28)',
                  '&:hover': {
                    background: `linear-gradient(135deg, ${T.greenMd} 0%, ${T.green} 100%)`,
                    boxShadow: '0 3px 12px rgba(10,107,62,0.32)',
                  },
                  '&.Mui-disabled': {
                    background: `${T.borderL}`,
                    color: T.muted,
                    boxShadow: 'none',
                    opacity: 1,
                  },
                }}
              >
                Apply filters
              </Button>
            </Box>
          </Box>
        </Grid>

        {/* Saved Searches Panel — light theme surfaces, compact type */}
        <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column', height: '76vh', bgcolor: T.white }}>
          <Box sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            borderBottom: `1px solid ${T.border}`,
            background: `linear-gradient(135deg, ${T.bg} 0%, #FFFFFF 100%)`,
          }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: '10px',
              background: `linear-gradient(135deg, ${T.green} 0%, ${T.greenMd} 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(10,107,62,0.22)',
              mr: 1,
            }}>
              <BookmarkBorderIcon sx={{ fontSize: 15, color: '#fff' }} />
            </Box>
            <Box>
              <Typography fontWeight={700} color={T.ink} sx={{ fontSize: '0.72rem', lineHeight: 1.25 }}>
                Saved searches
              </Typography>
              <Typography sx={{ fontSize: '0.62rem', color: T.muted, lineHeight: 1.25 }}>
                {statusCounts?.saved_searches?.length || 0} saved
              </Typography>
            </Box>
          </Box>

          {/* Saved Searches List */}
          <Box sx={{ overflow: 'auto', flex: 1, px: 1.25, py: 1, bgcolor: T.bodyBg }}>
            {statusCounts?.saved_searches?.length > 0 ? (
              statusCounts.saved_searches.map(search => {
                const isEditing = editingSearchId === search.id;
                const isExpanded = expandedSearchId === search.id;
                const criteriaCount = countSavedSearchCriteria(search.search_criteria);
                const savedSearchCardSx = {
                  mb: 1.25,
                  borderRadius: '10px',
                  border: `1px solid ${isExpanded ? T.border : T.borderL}`,
                  bgcolor: T.white,
                  boxShadow: isExpanded
                    ? '0 2px 10px rgba(10,107,62,0.07)'
                    : '0 1px 2px rgba(10,107,62,0.04)',
                  transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                  overflow: 'hidden',
                  '&:hover': {
                    boxShadow: isExpanded ? undefined : '0 2px 8px rgba(10,107,62,0.06)',
                  },
                };

                return (
                  <Box key={search.id} sx={savedSearchCardSx}>
                    {/* Header row: brighter surface when open, separated from editor below */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'stretch',
                        minHeight: 48,
                        bgcolor: isExpanded ? T.white : 'transparent',
                        borderBottom: isExpanded ? `1px solid ${T.border}` : 'none',
                        boxShadow: isExpanded ? 'inset 0 -1px 0 rgba(10,107,62,0.06)' : 'none',
                        transition: 'background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
                      }}
                    >
                      <Box
                        sx={{
                          width: 3,
                          flexShrink: 0,
                          bgcolor: isExpanded ? T.greenLt : (isEditing ? T.greenSf : T.borderL),
                          transition: 'background-color 0.2s ease',
                        }}
                      />
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          px: 1.25,
                          py: 1.1,
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          gap: 1,
                          bgcolor: 'transparent',
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              cursor: !isEditing ? 'pointer' : 'default',
                              color: isEditing ? T.green : T.ink2,
                              fontSize: '0.72rem',
                              fontWeight: 600,
                              lineHeight: 1.35,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              '&:hover': !isEditing ? { color: T.greenMd } : undefined,
                            }}
                            onClick={() => {
                              if (!isEditing) {
                                handleApplySavedSearch(search);
                              }
                            }}
                          >
                            {search.name}
                          </Typography>
                          <Typography
                            sx={{
                              display: 'block',
                              mt: 0.25,
                              fontSize: '0.62rem',
                              color: T.muted,
                              lineHeight: 1.35,
                            }}
                          >
                            {criteriaCount} filter{criteriaCount === 1 ? '' : 's'}
                          </Typography>
                        </Box>

                        <Stack direction="row" spacing={0.25} alignItems="center" sx={{ flexShrink: 0 }}>
                          <Tooltip title={isExpanded ? 'Collapse' : 'Expand'}>
                            <IconButton
                              size="small"
                              sx={{ p: 0.35, color: T.muted, '&:hover': { color: T.ink2, bgcolor: T.borderL } }}
                              onClick={() => handleExpandSearch(search.id)}
                            >
                              {isExpanded ? <KeyboardArrowUpIcon sx={{ fontSize: 16 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={isEditing ? 'Save changes' : 'Edit'}>
                            <IconButton
                              size="small"
                              sx={{
                                p: 0.35,
                                color: isEditing ? T.green : T.muted,
                                '&:hover': { color: T.green, bgcolor: T.greenSf },
                              }}
                              onClick={() => {
                                if (isEditing) {
                                  handleUpdateSearch();
                                } else {
                                  handleExpandSearch(search.id);
                                }
                              }}
                            >
                              {isEditing ? <SaveIcon sx={{ fontSize: 16 }} /> : <EditIcon sx={{ fontSize: 16 }} />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              sx={{ p: 0.35, color: T.muted, '&:hover': { color: '#EF4444', bgcolor: '#FEF2F2' } }}
                              onClick={() => handleDeleteSearch(search.id)}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Box>
                    </Box>

                    <Collapse in={isExpanded}>
                      <Box
                        sx={{
                          px: 1.25,
                          py: 1.5,
                          bgcolor: T.bg,
                          borderTop: 'none',
                        }}
                      >
                        {isEditing && (
                          <Box sx={{ mb: 1.5 }}>
                            <Typography sx={{ ...sectionHeaderStyles, color: T.muted, display: 'block', mb: 0.5, letterSpacing: '0.04em' }}>
                              Search Name
                            </Typography>
                            <TextField fullWidth size="small" value={editingName} onChange={(e) => setEditingName(e.target.value)} variant="outlined" sx={textFieldStyles} />
                          </Box>
                        )}
                        {isEditing && (
                          <Box sx={{ mb: 1.5 }}>
                            <Button
                              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                              onClick={handleAddFilterClick}
                              color="primary"
                              size="small"
                              sx={{ textTransform: 'none', fontSize: '0.72rem', minHeight: 28, borderRadius: '8px' }}
                            >
                              Add filter
                            </Button>
                          </Box>
                        )}

                        {Object.keys(editingFilters).map(filterKey => {
                          if (!defaultFilters || !defaultFilters[filterKey]) return null;

                          const filterOptions = defaultFilters[filterKey];
                          const filterValues = editingFilters[filterKey] || [];
                          const icon = getFilterIcon(filterKey);
                          const label = toTitleCase(filterKey);
                          const chipPalette = FILTER_ICON_COLORS[filterKey] || { color: T.green, bg: T.greenSf };

                          return (
                            <Box
                              key={filterKey}
                              sx={{
                                mb: 1.25,
                                p: 1.25,
                                borderRadius: '8px',
                                border: `1px solid ${T.borderL}`,
                                bgcolor: T.white,
                                boxShadow: '0 1px 2px rgba(10,107,62,0.04)',
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                                  {icon}
                                  <Typography sx={{ ml: 1, ...sectionHeaderStyles, color: T.ink2 }}>
                                    {label}
                                  </Typography>
                                </Box>

                                <IconButton size="small" sx={{ p: 0.35, color: T.muted, '&:hover': { color: '#EF4444' } }} onClick={() => handleRemoveFilter(filterKey)}>
                                  <DeleteIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Box>

                              {filterKey === 'budget' ? (
                                <Autocomplete
                                  multiple
                                  size="small"
                                  disableCloseOnSelect
                                  options={defaultFilters?.budget_ranges || []}
                                  value={(editingFilters.budget_ranges || []).map((v) => {
                                    const opts = defaultFilters?.budget_ranges || [];
                                    const raw = typeof v === 'object' && v !== null ? v.value ?? v : v;
                                    return opts.find((o) => (o?.value ?? o) === raw) || v;
                                  })}
                                  onChange={(_, newVal) => {
                                    const values = newVal.map((item) =>
                                      typeof item === 'object' && item?.value !== undefined ? item.value : item,
                                    );
                                    setEditingFilters((prev) => {
                                      const next = { ...prev, budget_ranges: values };
                                      delete next.budget;
                                      return next;
                                    });
                                  }}
                                  getOptionLabel={(o) => o?.label || String(o)}
                                  isOptionEqualToValue={(opt, val) => (opt?.value ?? opt) === (val?.value ?? val)}
                                  disabled={!isEditing}
                                  renderInput={(params) => (
                                    <TextField {...params} placeholder="Select budget range" size="small" sx={textFieldStyles} />
                                  )}
                                  sx={{ '& .MuiAutocomplete-tag': { maxWidth: '100%' } }}
                                />
                              ) : Array.isArray(filterOptions) ? (
                                <Autocomplete
                                  multiple
                                  size="small"
                                  options={
                                    filterKey === 'lead_stage'
                                      ? filterOptions.filter(
                                          (opt) => (opt.value || opt) !== 'follow_up'
                                        )
                                      : filterOptions
                                  }
                                  value={Array.isArray(filterValues) ? filterValues : []}
                                  onChange={(e, newValue) => {
                                    if (isEditing) {
                                      handleEditFilterChange(filterKey, newValue);
                                    }
                                  }}
                                  disabled={!isEditing}
                                  getOptionLabel={(option) => option.label || option}
                                  getOptionValue={(option) => option.value || option}
                                  isOptionEqualToValue={(option, value) =>
                                    (option.value || option) === (value.value || value)
                                  }
                                  slotProps={filterAutocompleteSlotProps}
                                  sx={{
                                    ...autocompleteStyles,
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: '8px',
                                      transition: 'box-shadow 0.15s, border-color 0.15s',
                                      '&.Mui-focused': {
                                        boxShadow: `0 0 0 3px ${chipPalette.color}15`,
                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: chipPalette.color },
                                      },
                                    },
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder={`Search ${label}`}
                                      variant="outlined"
                                      size="small"
                                      fullWidth
                                    />
                                  )}
                                  renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                      <Chip
                                        {...getTagProps({ index })}
                                        label={option.label || option}
                                        size="small"
                                        sx={{
                                          ...chipStyles,
                                          backgroundColor: chipPalette.bg,
                                          color: chipPalette.color,
                                          border: `1px solid ${chipPalette.color}22`,
                                          fontWeight: 500,
                                          '&:hover': { backgroundColor: `${chipPalette.color}18` },
                                          '& .MuiChip-deleteIcon': { color: chipPalette.color, opacity: 0.5, '&:hover': { opacity: 1 } },
                                        }}
                                        disabled={!isEditing}
                                      />
                                    ))
                                  }
                                />
                              ) : typeof filterOptions === 'object' ? (
                                <Autocomplete
                                  multiple
                                  size="small"
                                  options={Object.keys(filterOptions)}
                                  value={Array.isArray(filterValues) ? filterValues : []}
                                  onChange={(e, newValue) => {
                                    if (isEditing) {
                                      handleEditFilterChange(filterKey, newValue);
                                    }
                                  }}
                                  disabled={!isEditing}
                                  slotProps={filterAutocompleteSlotProps}
                                  sx={{
                                    ...autocompleteStyles,
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: '8px',
                                      transition: 'box-shadow 0.15s, border-color 0.15s',
                                      '&.Mui-focused': {
                                        boxShadow: `0 0 0 3px ${chipPalette.color}15`,
                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: chipPalette.color },
                                      },
                                    },
                                  }}
                                  getOptionLabel={(option) => filterOptions[option] || option}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder={`Select ${label}`}
                                      variant="outlined"
                                      size="small"
                                      fullWidth
                                    />
                                  )}
                                  renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                      <Chip
                                        {...getTagProps({ index })}
                                        label={filterOptions[option] || option}
                                        size="small"
                                        sx={{
                                          ...chipStyles,
                                          backgroundColor: chipPalette.bg,
                                          color: chipPalette.color,
                                          border: `1px solid ${chipPalette.color}22`,
                                          fontWeight: 500,
                                          '& .MuiChip-deleteIcon': { color: chipPalette.color, opacity: 0.5, '&:hover': { opacity: 1 } },
                                        }}
                                        disabled={!isEditing}
                                      />
                                    ))
                                  }
                                />
                              ) : null}
                            </Box>
                          );
                        })}

                        <Box
                          sx={{
                            p: 1.25,
                            borderRadius: '8px',
                            border: `1px solid ${T.borderL}`,
                            bgcolor: T.white,
                            boxShadow: '0 1px 2px rgba(10,107,62,0.04)',
                          }}
                        >
                          {editingFilters.agent_id ? (
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75, justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  {getFilterIcon('agent_id')}
                                  <Typography sx={{ ml: 1, ...sectionHeaderStyles, color: T.ink2 }}>
                                    Agent
                                  </Typography>
                                </Box>
                                {isEditing && (
                                  <IconButton size="small" sx={{ p: 0.35, color: T.muted, '&:hover': { color: '#EF4444' } }} onClick={() => handleRemoveFilter('agent_id')}>
                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                )}
                              </Box>

                              <AgentSelect
                                agents={agents || []}
                                value={editingFilters.agent_id || []}
                                onChange={(agentIds) => handleEditFilterChange('agent_id', agentIds)}
                                label="Select Agents"
                                multiple={true}
                                size="small"
                                themeVariant="lead"
                                disabled={!isEditing}
                                sx={{
                                  mt: 0,
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    minHeight: '30px',
                                    '&.Mui-focused': { boxShadow: `0 0 0 3px rgba(139,92,246,0.12)` },
                                  },
                                }}
                              />
                            </Box>
                          ) : (
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
                                {getFilterIcon('agent_id')}
                                <Typography sx={{ ml: 1, ...sectionHeaderStyles, color: T.ink2 }}>Agent</Typography>
                              </Box>
                              <Button
                                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                                onClick={() => handleAddFilter('agent_id')}
                                color="primary"
                                size="small"
                                sx={{ textTransform: 'none', fontSize: '0.72rem', minHeight: 28, borderRadius: '8px' }}
                                disabled={!isEditing}
                              >
                                Add agent filter
                              </Button>
                            </Box>
                          )}
                        </Box>

                        <Box
                          sx={{
                            mt: 1.5,
                            pt: 1.5,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 1,
                            borderTop: `1px dashed ${T.borderL}`,
                          }}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => { setExpandedSearchId(null); setEditingSearchId(null); setEditingName(''); setEditingFilters({}); }}
                            sx={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.72rem', minHeight: 30, px: 1.5, borderColor: T.border, color: T.ink2, textTransform: 'none' }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={handleUpdateSearch}
                            disabled={updatingSearch}
                            sx={{
                              borderRadius: '8px',
                              fontWeight: 600,
                              fontSize: '0.72rem',
                              minHeight: 30,
                              px: 2,
                              textTransform: 'none',
                              bgcolor: T.green,
                              boxShadow: '0 2px 8px rgba(10,107,62,0.2)',
                              '&:hover': { bgcolor: T.greenMd },
                            }}
                          >
                            {updatingSearch ? 'Saving...' : 'Save changes'}
                          </Button>
                        </Box>
                      </Box>
                    </Collapse>
                  </Box>
                );
              })
            ) : (
              <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
                <Box sx={{
                  width: 48, height: 48, borderRadius: '14px',
                  background: `linear-gradient(135deg, ${T.greenLt} 0%, ${T.bg} 100%)`,
                  border: `1px solid ${T.borderL}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5,
                }}>
                  <BookmarkBorderIcon sx={{ fontSize: 22, color: T.green }} />
                </Box>
                <Typography sx={{ mb: 0.5, fontSize: '0.72rem', fontWeight: 600, color: T.ink2 }}>No saved searches</Typography>
                <Typography sx={{ fontSize: '0.62rem', color: T.muted, textAlign: 'center', maxWidth: 200, lineHeight: 1.45 }}>
                  Save your frequently used filter combinations for quick access
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Add Filter Menu */}
      <Menu
        anchorEl={addFilterAnchorEl}
        open={Boolean(addFilterAnchorEl)}
        onClose={handleAddFilterClose}
      >
        {getAvailableFilters().map(filterKey => (
          <MenuItem
            key={filterKey}
            onClick={() => handleAddFilter(filterKey)}
          >
            <ListItemIcon>
              {getFilterIcon(filterKey)}
            </ListItemIcon>
            <ListItemText primary={toTitleCase(filterKey)} />
          </MenuItem>
        ))}
        {getAvailableFilters().length === 0 && (
          <MenuItem disabled>
            <ListItemText primary="All filters already added" />
          </MenuItem>
        )}
      </Menu>
    </Popover>
  );
};

export default LeadFilters; 